import { Injectable } from '@angular/core';

import { TrColumn } from '@shared/restaurant/models';
import { LocalStorageService } from '@tablesready/frontend-storage';


/**
 * Per-column device state persisted to localStorage.
 *
 * Every field is written explicitly, never as "absent means default": a payload
 * describes the whole state of a column, so adding a column (or changing what its
 * default happens to be) can never silently reinterpret what an existing user
 * already saved. `null` means "no explicit value" and is stored as such.
 */
export interface TrColumnState {
  key: string;
  hidden: boolean;
  pin: 'start' | 'end' | null;
  /** Device-local only — widths are never synced to the backend. */
  width: number | null;
}

/** The stored payload for one table on one device. Column order is array order. */
export interface TrTableState {
  v: number;
  columns: TrColumnState[];
}

/**
 * Device working copy of table column state (order, visibility, pin, width).
 *
 * Every interactive change writes straight to localStorage, keyed by location and
 * table id (`tr-table-settings:<locationId>:<tableId>`), so the layout survives a
 * refresh, a token refresh, a shared login, and the Capacitor iOS webview. Nothing
 * here talks to the backend: the structure of the table always comes from the
 * host's freshly built column set, and only the user's *device* preferences come
 * from storage.
 *
 * Two rules keep stored state safe to evolve:
 * - **Explicit, never a diff.** Each entry carries a concrete value for every
 *   field ({@link TrColumnState}), so a column added to the app later can't change
 *   the meaning of state that was written before it existed.
 * - **Never destructive.** Reads normalise older payloads instead of discarding
 *   them, and a save keeps the entries of columns the host isn't building right
 *   now (a service turned off, custom fields not loaded yet, an async
 *   `buildColumns()` that has only run on partial data) so a stray resize can't
 *   erase the layout of a column that is merely temporarily absent.
 */
@Injectable({ providedIn: 'root' })
export class TrTableStateService {
  private static readonly PREFIX = 'tr-table-settings';
  /**
   * Bump only for a shape change older payloads genuinely can't be read as. Adding
   * a *column* never needs a bump, and adding a *field* shouldn't either — give it
   * an explicit default in {@link normalizeColumn} and older payloads keep working.
   */
  private static readonly VERSION = 2;
  /**
   * Ceiling on stored entries. Retained-but-absent keys would otherwise grow
   * forever as custom fields (`cf_<id>`) are created and deleted; past this many,
   * the absent ones are dropped from the end.
   */
  private static readonly MAX_COLUMNS = 200;

  constructor(private readonly storage: LocalStorageService) {}

  /** Storage key for one table on one location. Null when either id is missing. */
  keyFor(locationId?: string | null, tableId?: string | null): string | null {
    if (!locationId || !tableId) {
      return null;
    }

    return `${TrTableStateService.PREFIX}:${locationId}:${tableId}`;
  }

  /**
   * The stored state, normalised to the current shape.
   *
   * Payloads from an older version are upgraded in place of being thrown away —
   * an omitted field becomes its explicit default rather than a lost layout.
   * Only a payload that isn't recognisable at all yields null.
   */
  read(locationId?: string | null, tableId?: string | null): TrTableState | null {
    const key = this.keyFor(locationId, tableId);
    if (!key) {
      return null;
    }
    const raw = this.storage.get<Partial<TrTableState>>(key);
    if (!raw || typeof raw.v !== 'number' || !Array.isArray(raw.columns)) {
      return null;
    }
    // A payload from a *newer* app version may mean things this build doesn't
    // understand — leave it alone rather than rewrite it into a lossy shape.
    if (raw.v > TrTableStateService.VERSION) {
      return null;
    }

    return {
      v: TrTableStateService.VERSION,
      columns: raw.columns
        .filter((col) => !!col && typeof col.key === 'string')
        .map((col) => this.normalizeColumn(col)),
    };
  }

  /**
   * Persist the table's current column order + per-column state.
   *
   * Columns the host isn't currently building keep their stored entry, anchored
   * after their nearest surviving neighbour, so their order/visibility/pin/width
   * comes back with them.
   */
  save(locationId: string | null | undefined, tableId: string | null | undefined, columns: TrColumn[]): void {
    const key = this.keyFor(locationId, tableId);
    if (!key) {
      return;
    }
    const built = columns.map((col) => this.normalizeColumn(col));
    const previous = this.read(locationId, tableId)?.columns ?? [];
    this.storage.set(key, {
      v: TrTableStateService.VERSION,
      columns: this.trim(this.retainAbsent(built, previous), built),
    });
  }

  /** Drop the device copy entirely — order, visibility, pin and widths. */
  clear(locationId?: string | null, tableId?: string | null): void {
    const key = this.keyFor(locationId, tableId);
    if (key) {
      this.storage.remove(key);
    }
  }

  /**
   * Merge the stored device state onto a freshly built column set.
   *
   * Structure comes from `columns` (which columns exist at all, their labels and
   * chrome); order, visibility, pin and width come from storage. Stored keys the
   * host doesn't build are ignored here (they stay in storage — see
   * {@link save}); columns the host added since the state was written keep their
   * default position, anchored after their nearest surviving predecessor, and
   * their default visibility/pin/width. Returns a new array of new column objects
   * — the caller's objects are never mutated.
   */
  apply<T extends TrColumn>(columns: T[], state: TrTableState | null): T[] {
    const clones = columns.map((col) => ({ ...col }));
    if (!state?.columns.length) {
      return clones;
    }

    const byKey = new Map(clones.map((col) => [col.key, col]));
    const stored = new Map(state.columns.map((col) => [col.key, col]));

    // Stored order first, keeping only columns the host still builds.
    const ordered = state.columns
      .map((s) => byKey.get(s.key))
      .filter((col): col is T & { key: string } => !!col);

    // Splice in columns added since the state was written, at their default spot.
    clones.forEach((col, defaultIndex) => {
      if (stored.has(col.key)) {
        return;
      }
      let at = 0;
      for (let i = defaultIndex - 1; i >= 0; i--) {
        const found = ordered.findIndex((c) => c.key === clones[i].key);
        if (found >= 0) {
          at = found + 1;
          break;
        }
      }
      ordered.splice(at, 0, col);
    });

    for (const col of ordered) {
      const saved = stored.get(col.key);
      if (!saved) {
        continue;
      }
      col.hidden = saved.hidden;
      col.pin = saved.pin ?? undefined;
      col.width = saved.width ?? undefined;
    }

    // Never restore a state that leaves nothing to render.
    if (ordered.length && ordered.every((col) => col.hidden)) {
      ordered.forEach((col) => (col.hidden = false));
    }

    return ordered;
  }

  /**
   * One column's state with a concrete value for every field.
   *
   * The single place that decides what an unset field means, for both a live
   * {@link TrColumn} being saved and an older stored entry being read. A new field
   * belongs here with its default, not behind a version bump.
   */
  private normalizeColumn(col: {
    key: string;
    hidden?: boolean;
    pin?: 'start' | 'end' | null;
    width?: number | null;
  }): TrColumnState {
    return {
      key: col.key,
      hidden: !!col.hidden,
      pin: col.pin === 'start' || col.pin === 'end' ? col.pin : null,
      width: typeof col.width === 'number' && col.width > 0 ? col.width : null,
    };
  }

  /**
   * Keep stored entries for keys the host isn't building right now, each anchored
   * after its nearest surviving predecessor in the stored order — the mirror of
   * how {@link apply} slots in columns it has never seen.
   */
  private retainAbsent(built: TrColumnState[], previous: TrColumnState[]): TrColumnState[] {
    const keys = new Set(built.map((col) => col.key));
    const merged = [...built];

    previous.forEach((old, oldIndex) => {
      if (keys.has(old.key)) {
        return;
      }
      let at = 0;
      for (let i = oldIndex - 1; i >= 0; i--) {
        const found = merged.findIndex((col) => col.key === previous[i].key);
        if (found >= 0) {
          at = found + 1;
          break;
        }
      }
      merged.splice(at, 0, old);
      keys.add(old.key);
    });

    return merged;
  }

  /** Cap the payload, sacrificing retained-but-absent entries from the end first. */
  private trim(merged: TrColumnState[], built: TrColumnState[]): TrColumnState[] {
    if (merged.length <= TrTableStateService.MAX_COLUMNS) {
      return merged;
    }
    const keys = new Set(built.map((col) => col.key));
    const trimmed = [...merged];
    for (let i = trimmed.length - 1; i >= 0 && trimmed.length > TrTableStateService.MAX_COLUMNS; i--) {
      if (!keys.has(trimmed[i].key)) {
        trimmed.splice(i, 1);
      }
    }

    return trimmed.slice(0, TrTableStateService.MAX_COLUMNS);
  }
}
